import {useCallback, useRef, useState} from 'react';

import {FocusSection} from '../types/FocusSection';

export interface AutoScrollConfig {
  enabled: boolean;
  behavior: 'smooth' | 'instant';
  block: 'start' | 'center' | 'end' | 'nearest';
  inline: 'start' | 'center' | 'end' | 'nearest';
  scrollThreshold: number; // pixels from edge to trigger scroll
}

export interface TimePickerAutoScrollState {
  isAutoScrollEnabled: boolean;
  lastScrollTarget: HTMLElement | null;
  scrollAnimationFrame: number | null;
}

export interface ScrollTarget {
  element: HTMLElement;
  container: HTMLElement;
  section: 'hours' | 'minutes';
  index: number;
  isVisible: boolean;
}

export interface ScrollOperation {
  target: ScrollTarget;
  direction: 'up' | 'down';
  trigger: 'keyboard_navigation';
  timestamp: number;
}

export interface UseTimePickerAutoScrollProps {
  focusedSection: FocusSection | null;
  focusedIndex: number;
  isDropdownOpen: boolean;
  hourElements: HTMLElement[];
  minuteElements: HTMLElement[];
  config?: Partial<AutoScrollConfig>;
  onScrollStart?: (operation: ScrollOperation) => void;
  onScrollComplete?: (operation: ScrollOperation) => void;
}

export interface UseTimePickerAutoScrollReturn {
  scrollToFocusedItem: (section: FocusSection, index: number) => void;
  isScrolling: boolean;
  cancelScrollAnimation: () => void;
}

const DEFAULT_SCROLL_CONFIG: AutoScrollConfig = {
  enabled: true,
  behavior: 'smooth',
  block: 'nearest',
  inline: 'nearest',
  scrollThreshold: 10,
};

// Scroll utility functions
interface ScrollUtils {
  isElementInView: (element: HTMLElement, container: HTMLElement) => boolean;
  getScrollPosition: (
    element: HTMLElement,
    container: HTMLElement
  ) => 'above' | 'below' | 'visible';
  scrollElementIntoView: (element: HTMLElement, config: AutoScrollConfig) => Promise<void>;
  calculateScrollOffset: (element: HTMLElement, container: HTMLElement) => number;
}

const createScrollUtils = (): ScrollUtils => ({
  isElementInView: (element: HTMLElement, container: HTMLElement): boolean => {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
  },

  getScrollPosition: (
    element: HTMLElement,
    container: HTMLElement
  ): 'above' | 'below' | 'visible' => {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (elementRect.bottom < containerRect.top) {
      return 'above';
    }
    if (elementRect.top > containerRect.bottom) {
      return 'below';
    }
    return 'visible';
  },

  scrollElementIntoView: (element: HTMLElement, config: AutoScrollConfig): Promise<void> => {
    return new Promise((resolve) => {
      element.scrollIntoView({
        behavior: config.behavior,
        block: config.block,
        inline: config.inline,
      });

      // Use requestAnimationFrame to detect scroll completion
      requestAnimationFrame(() => resolve());
    });
  },

  calculateScrollOffset: (element: HTMLElement, container: HTMLElement): number => {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (elementRect.top < containerRect.top) {
      return elementRect.top - containerRect.top;
    }
    if (elementRect.bottom > containerRect.bottom) {
      return elementRect.bottom - containerRect.bottom;
    }
    return 0;
  },
});

export function useTimePickerAutoScroll({
  focusedSection,
  focusedIndex,
  isDropdownOpen,
  hourElements,
  minuteElements,
  config = {},
  onScrollStart,
  onScrollComplete,
}: UseTimePickerAutoScrollProps): UseTimePickerAutoScrollReturn {
  const finalConfig = {...DEFAULT_SCROLL_CONFIG, ...config};

  const [scrollState, setScrollState] = useState<TimePickerAutoScrollState>({
    isAutoScrollEnabled: finalConfig.enabled,
    lastScrollTarget: null,
    scrollAnimationFrame: null,
  });

  const scrollUtils = useRef(createScrollUtils()).current;

  const cancelScrollAnimation = useCallback(() => {
    if (scrollState.scrollAnimationFrame) {
      cancelAnimationFrame(scrollState.scrollAnimationFrame);
      setScrollState((prev) => ({
        ...prev,
        scrollAnimationFrame: null,
      }));
    }
  }, [scrollState.scrollAnimationFrame]);

  const createScrollTarget = useCallback(
    (section: FocusSection, index: number): ScrollTarget | null => {
      const elements = section === 'hours' ? hourElements : minuteElements;
      const element = elements[index];

      if (!element) {
        return null;
      }

      // Find the container (List component)
      const container = element.closest('[role="listbox"]') as HTMLElement;
      if (!container) {
        return null;
      }

      const isVisible = scrollUtils.isElementInView(element, container);

      return {
        element,
        container,
        section,
        index,
        isVisible,
      };
    },
    [hourElements, minuteElements, scrollUtils]
  );

  const scrollToFocusedItem = useCallback(
    (section: FocusSection, index: number) => {
      if (!finalConfig.enabled || !isDropdownOpen || index < 0) {
        return;
      }

      // Cancel any ongoing scroll animation
      cancelScrollAnimation();

      const scrollTarget = createScrollTarget(section, index);
      if (!scrollTarget) {
        return;
      }

      // Check if element is already visible
      if (scrollTarget.isVisible) {
        return;
      }

      // Determine scroll direction
      const scrollPosition = scrollUtils.getScrollPosition(
        scrollTarget.element,
        scrollTarget.container
      );
      const direction = scrollPosition === 'above' ? 'up' : 'down';

      const scrollOperation: ScrollOperation = {
        target: scrollTarget,
        direction,
        trigger: 'keyboard_navigation',
        timestamp: Date.now(),
      };

      // Notify scroll start
      onScrollStart?.(scrollOperation);

      // Perform the scroll operation
      const animationFrame = requestAnimationFrame(async () => {
        try {
          await scrollUtils.scrollElementIntoView(scrollTarget.element, finalConfig);

          // Update state after successful scroll
          setScrollState((prev) => ({
            ...prev,
            lastScrollTarget: scrollTarget.element,
            scrollAnimationFrame: null,
          }));

          // Notify scroll completion
          onScrollComplete?.(scrollOperation);
        } catch (error) {
          // Handle scroll errors gracefully
          console.warn('Auto-scroll failed:', error);

          setScrollState((prev) => ({
            ...prev,
            scrollAnimationFrame: null,
          }));
        }
      });

      setScrollState((prev) => ({
        ...prev,
        scrollAnimationFrame: animationFrame,
      }));
    },
    [
      finalConfig,
      isDropdownOpen,
      cancelScrollAnimation,
      createScrollTarget,
      scrollUtils,
      onScrollStart,
      onScrollComplete,
    ]
  );

  const isScrolling = scrollState.scrollAnimationFrame !== null;

  return {
    scrollToFocusedItem,
    isScrolling,
    cancelScrollAnimation,
  };
}
